// src/pages/Terms.js
import React from "react";
import { useSEO } from "../hooks/useSEO";
import Card from "../components/ui/Card";

export default function Terms() {
  useSEO({
    title: "Terms of Service",
    description: "Read the Terms of Service for Predict90. Understand the rules, policies, and guidelines for using our prediction platform.",
    keywords: "terms of service, terms and conditions, legal, Predict90",
  });

  return (
    <div className="min-h-screen bg-gray-50 py-10 md:py-14">
      <div className="max-w-4xl mx-auto px-6">
        <Card>
          <div className="prose prose-sm md:prose-base max-w-none">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
            <p className="text-sm text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-4">
                By accessing and using Predict90 ("the Platform", "we", "us", "our"), 
                you accept and agree to be bound by these Terms of Service ("Terms"). If you do not agree 
                to these Terms, please do not use the Platform.
              </p>
              <p className="text-gray-700">
                These Terms constitute a legally binding agreement between you and Predict90. 
                We reserve the right to modify these Terms at any time, and such modifications will be effective 
                immediately upon posting on the Platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Company Information</h2>
              <p className="text-gray-700 mb-4">
                Predict90 operates as a skill-based prediction platform service.
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-gray-900 font-semibold mb-2">⚠️ Important Notice:</p>
                <p className="text-gray-700 text-sm">
                  The business information below contains placeholders and will be updated once business 
                  registration is completed. These details are subject to change and will be reflected 
                  in an updated version of these Terms.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-gray-700 mb-2"><strong>Business Name:</strong> Predict90</p>
                <p className="text-gray-700 mb-2"><strong>Business Address:</strong> [To be updated]</p>
                <p className="text-gray-700 mb-2"><strong>Registration Number:</strong> [To be updated]</p>
                <p className="text-gray-700 mb-2"><strong>GST Number:</strong> [To be updated - if applicable]</p>
                <p className="text-gray-700"><strong>Contact Email:</strong> <a href="mailto:support@predict90.com" className="text-blue-600 hover:underline">support@predict90.com</a></p>
              </div>
              <p className="text-sm text-gray-600 italic">
                Note: Business registration details are placeholders and will be updated upon completion 
                of the business registration process. The "Last updated" date at the top of this page 
                will reflect when these details are finalized.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                Predict90 is a skill-based prediction platform where users make predictions 
                about football matches based on their knowledge, analysis, and understanding of the sport. 
                Users compete with each other in leagues and leaderboards based on the accuracy of their predictions.
              </p>
              <p className="text-gray-700 mb-4">
                <strong className="text-gray-900">IMPORTANT: This Platform is NOT a betting, gambling, or wagering platform.</strong>
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Users make predictions based on skill and knowledge, not chance or luck</li>
                <li>No real-money bets, wagers, or stakes are placed on the Platform</li>
                <li>No payouts, winnings, or returns are made based on prediction outcomes</li>
                <li>This is a skill-based competition platform, similar to fantasy sports platforms</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. No Betting or Gambling</h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-gray-900 font-semibold mb-2">CRITICAL CLAUSE:</p>
                <p className="text-gray-700">
                  This Platform does NOT involve betting, gambling, wagering, or any form of real-money
                  staking. Predict90 is currently free to use for core features. No money is returned,
                  paid out, or distributed based on prediction outcomes or results. If optional paid
                  features are introduced in the future, any such fees would be for platform access
                  only—not bets, wagers, or stakes. This is a skill-based competition platform, not a
                  betting or gambling service.
                </p>
              </div>
              <p className="text-gray-700">
                Access to predictions, leaderboards, and related features is provided without requiring
                payment for the free tier. Should paid options ever be offered, they would be for
                additional or extended services only—not wagering.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. User Accounts</h2>
              <p className="text-gray-700 mb-4">
                To use the Platform, you must create an account. You agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and update your account information to keep it accurate</li>
                <li>Maintain the security of your account credentials</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>
              <p className="text-gray-700">
                You must be at least 18 years old to use this Platform. By creating an account, you 
                represent and warrant that you are at least 18 years of age.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. User Conduct</h2>
              <p className="text-gray-700 mb-4">You agree NOT to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Use the Platform for any illegal or unauthorized purpose</li>
                <li>Violate any laws in your jurisdiction</li>
                <li>Interfere with or disrupt the Platform or servers</li>
                <li>Attempt to gain unauthorized access to any part of the Platform</li>
                <li>Use automated systems (bots, scrapers) to access the Platform</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Post spam, malicious content, or inappropriate material</li>
                <li>Impersonate any person or entity</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Predictions and Competition</h2>
              <p className="text-gray-700 mb-4">
                Users make predictions about football matches based on their skill, knowledge, and analysis. 
                Predictions are evaluated based on accuracy, and users are ranked on leaderboards accordingly.
              </p>
              <p className="text-gray-700 mb-4">
                We reserve the right to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Modify or update prediction rules and scoring systems</li>
                <li>Correct errors in scoring or leaderboard calculations</li>
                <li>Disqualify predictions that violate these Terms</li>
                <li>Update match results and fixture information</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Fees, subscriptions, and refunds</h2>
              <p className="text-gray-700 mb-4">
                <strong>Current service:</strong> Predict90 is offered free of charge for standard account and
                prediction features. No payment is required to create an account or participate in the core
                competition.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Future paid options:</strong> We may introduce optional subscription or one-time
                fees for additional features. If we do, we will update these Terms, describe what is included,
                and obtain your consent before charging you. Any such fees would be for platform access or
                extras only—not for betting or gambling.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Refunds:</strong> If you purchase a paid offering in the future, refund terms will be
                stated at checkout and in an updated version of this section. For questions, contact{" "}
                <a href="mailto:support@predict90.com" className="text-blue-600 hover:underline">support@predict90.com</a>.
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">8.1 Chargebacks</h3>
              <p className="text-gray-700">
                If you make a payment in the future and initiate a chargeback or dispute, we may suspend your
                account pending resolution. We encourage you to contact us first to resolve billing issues.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                The Platform, including its design, features, content, and functionality, is owned by 
                Predict90 and protected by copyright, trademark, and other intellectual 
                property laws.
              </p>
              <p className="text-gray-700">
                You may not copy, modify, distribute, sell, or lease any part of the Platform without 
                our express written permission. You retain ownership of your predictions and user-generated 
                content, but grant us a license to use, display, and distribute such content on the Platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" 
                WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
              </p>
              <p className="text-gray-700 mb-4">
                We shall not be liable for any indirect, incidental, special, consequential, or punitive 
                damages, including but not limited to loss of profits, data, or use, arising out of or 
                related to your use of the Platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Termination</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to suspend or terminate your account at any time, with or without 
                notice, for any reason, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Violation of these Terms</li>
                <li>Fraudulent or illegal activity</li>
                <li>Abuse of the Platform or other users</li>
                <li>Extended periods of inactivity</li>
              </ul>
              <p className="text-gray-700">
                You may terminate your account at any time by contacting us or using account deletion 
                features (if available).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these Terms at any time. Material changes will be notified 
                via email or Platform notification. Continued use of the Platform after changes constitutes 
                acceptance of the modified Terms.
              </p>
              <p className="text-gray-700">
                The "Last updated" date at the top of this page indicates when these Terms were last revised.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Governing Law</h2>
              <p className="text-gray-700">
                These Terms shall be governed by and construed in accordance with the laws of India, 
                without regard to its conflict of law provisions. Any disputes arising from these Terms 
                shall be subject to the exclusive jurisdiction of the courts in India.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">14. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms, please contact us:
              </p>
              <p className="text-gray-700">
                Email: <a href="mailto:support@predict90.com" className="text-blue-600 hover:underline">support@predict90.com</a>
              </p>
            </section>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                By using Predict90, you acknowledge that you have read, understood, 
                and agree to be bound by these Terms of Service.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
